import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { formatINR } from "../lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrendIcon = ({ trend }) => {
  if (trend === "up") return <TrendingUp size={13} className="text-[#2FB999]" />;
  if (trend === "down") return <TrendingDown size={13} className="text-[#C0392B]" />;
  return <Minus size={13} className="text-[#8A8A82]" />;
};

const trendColor = (t) => (t === "up" ? "#2FB999" : t === "down" ? "#C0392B" : "#8A8A82");

const RateItem = ({ r }) => (
  <span className="inline-flex items-center gap-2 px-6 border-r border-white/10">
    <span className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#B87333]">{r.label}</span>
    <span className="font-serif text-sm text-[#F5F5F0]">{formatINR(r.price)}</span>
    <span className="font-body text-[10px] text-[#8A8A82]">{r.unit}</span>
    <span className="inline-flex items-center gap-1 font-grotesk text-[10px]" style={{ color: trendColor(r.trend) }}>
      <TrendIcon trend={r.trend} />
      {r.change_pct > 0 ? "+" : ""}{r.change_pct}%
    </span>
  </span>
);

export const PriceFlash = () => {
  const [rates, setRates] = useState([]);
  useEffect(() => { axios.get(`${API}/rates`).then((r) => setRates(r.data.filter((x) => x.group !== "metal_index"))).catch(() => {}); }, []);
  if (rates.length === 0) return null;
  const loop = [...rates, ...rates];
  return (
    <div data-testid="price-flash" className="border-y border-white/10 bg-[#0b0b0b] overflow-hidden">
      <div className="max-w-[1600px] mx-auto flex items-stretch">
        <div className="shrink-0 flex items-center gap-2 px-5 py-2.5 bg-[#B87333] text-black">
          <span className="w-2 h-2 rounded-full bg-black/70 animate-pulse" />
          <span className="font-grotesk text-[10px] uppercase tracking-[0.2em] font-bold">Today's Rates</span>
        </div>
        <div className="vinci-marquee-wrap flex-1 overflow-hidden py-2.5">
          <div className="vinci-marquee-track">
            {loop.map((r, i) => <RateItem key={`${r.category}-${i}`} r={r} />)}
          </div>
        </div>
      </div>
    </div>
  );
};

// Compact rates board for prominent placement
export const RatesBoard = () => {
  const [rates, setRates] = useState([]);
  const [updated, setUpdated] = useState(null);
  useEffect(() => {
    axios.get(`${API}/rates`).then((r) => {
      setRates(r.data);
      const u = r.data.map((x) => x.updated_at).filter(Boolean).sort().pop();
      setUpdated(u);
    }).catch(() => {});
  }, []);
  if (rates.length === 0) return null;
  return (
    <div data-testid="rates-board">
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-3">
          <span className="w-2 h-2 rounded-full bg-[#2FB999] animate-pulse" />
          <h2 className="font-serif text-2xl md:text-3xl">Today's indicative rates</h2>
        </div>
        {updated && <span className="font-body text-xs text-[#8A8A82]">Updated {new Date(updated).toLocaleString("en-IN")}</span>}
      </div>
      <div className="flex justify-end -mt-3 mb-3">
        <Link to="/trends" data-testid="view-trends-link" className="inline-flex items-center gap-1.5 font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#B87333] hover:underline">
          View price trends →
        </Link>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border border-white/10 rounded-sm overflow-hidden">
        {rates.map((r) => (
          <div key={r.category} data-testid={`rate-${r.category}`} className="bg-[#070707] p-5">
            <div className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#8A8A82]">{r.label}</div>
            <div className="font-serif text-2xl mt-2">{formatINR(r.price)}</div>
            <div className="font-body text-[10px] text-[#8A8A82]">{r.unit}</div>
            <div className="inline-flex items-center gap-1 mt-2 font-grotesk text-[10px] uppercase tracking-[0.14em]" style={{ color: trendColor(r.trend) }}>
              <TrendIcon trend={r.trend} />
              {r.change_pct > 0 ? "+" : ""}{r.change_pct}%
            </div>
          </div>
        ))}
      </div>
      <p className="font-body text-[10px] text-[#6f6f68] mt-3">Indicative daily rates. Final price is confirmed at checkout against live stock and your tier.</p>
    </div>
  );
};
